/**
 * The root route. A wiring declaration only.
 *
 * Its one job is the session-resolution boundary the authentication ADR §3 requires: no route
 * decides anything — and nothing renders — until the persisted session has settled. Awaiting it
 * here rather than in each route means there is exactly one place that can get it wrong, and the
 * promise is single-flight, so this costs one resolution per page load.
 *
 * A cold start with nothing in storage settles without a network call, so the common unauthenticated
 * case pays nothing for this.
 */

import { useEffect, useState } from 'react';
import { Outlet, createRootRoute } from '@tanstack/react-router';

import { awaitSessionResolved } from '~/entities/session';
import { useDefaultPaintOutsideTheApp } from '~/app/lib/brandPaint';
import { markAppPainted } from '~/app/lib/firstPaint';
import { isPhone } from '~/app/lib/phoneGate';
import { PhoneOnlyScreen } from '~/app/ui/PhoneOnlyScreen';

function RootScreen() {
  // A load that settles on a pre-session screen wears the default Flame — see brandPaint.ts.
  useDefaultPaintOutsideTheApp();

  // After the first commit, every later mount of the shell is a client-side arrival rather than
  // a document load — the distinction `AppShell`'s focus announcement needs (see firstPaint.ts).
  useEffect(() => {
    markAppPainted();
  }, []);

  // A phone gets the "use the mobile app" screen before sign-in, not after — see `app/lib/phoneGate.ts`.
  // Decided once, on mount: it is a fact about the device, and turning it must not swap the app in.
  const [onPhone] = useState(() =>
    isPhone(
      typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : undefined,
      typeof window.screen === 'object' ? window.screen : undefined,
    ),
  );
  if (onPhone) return <PhoneOnlyScreen />;

  return <Outlet />;
}

export const Route = createRootRoute({
  beforeLoad: () => awaitSessionResolved(),
  component: RootScreen,
});
